// src/components/OffersCarousel.tsx
import { useEffect, useRef } from 'react';
import { Offer } from '../types';
import { useCurrencySymbol } from '../lib/currency';
import { getFullImageUrl } from '../lib/api';
import { Tag, Megaphone } from 'lucide-react';

interface OffersCarouselProps {
  offers: Offer[];
  onOfferClick?: (offer: Offer) => void;
}

export function OffersCarousel({ offers, onOfferClick }: OffersCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const currencySymbol = useCurrencySymbol();

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || offers.length === 0) return;

    let scrollInterval: NodeJS.Timeout;
    let isPaused = false;

    const startAutoScroll = () => {
      scrollInterval = setInterval(() => {
        if (!isPaused && scrollContainer) {
          const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
          const currentScroll = scrollContainer.scrollLeft;

          if (currentScroll >= maxScroll) {
            scrollContainer.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            scrollContainer.scrollBy({ left: 300, behavior: 'smooth' });
          }
        }
      }, 3000);
    };

    const handleMouseEnter = () => { isPaused = true; };
    const handleMouseLeave = () => { isPaused = false; };

    scrollContainer.addEventListener('mouseenter', handleMouseEnter);
    scrollContainer.addEventListener('mouseleave', handleMouseLeave);
    scrollContainer.addEventListener('touchstart', handleMouseEnter);
    scrollContainer.addEventListener('touchend', handleMouseLeave);

    startAutoScroll();

    return () => {
      clearInterval(scrollInterval);
      scrollContainer.removeEventListener('mouseenter', handleMouseEnter);
      scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
      scrollContainer.removeEventListener('touchstart', handleMouseEnter);
      scrollContainer.removeEventListener('touchend', handleMouseLeave);
    };
  }, [offers.length]);

  if (!offers || offers.length === 0) return null;

  const getImageSrc = (offer: Offer) => {
    if (offer.image_url) return getFullImageUrl(offer.image_url);
    if (offer.image) return getFullImageUrl(offer.image);
    return null;
  };

  return (
    <div ref={scrollRef} className="overflow-x-auto scrollbar-hide -mx-4 px-4">
      <div className="flex gap-3 pb-2">
        {offers.map((offer) => {
          const isZeroPriceOffer = offer.price === 0.0;
          const imageSrc = getImageSrc(offer);

          return (
            <div
              key={offer.uuid}
              onClick={() => onOfferClick?.(offer)}
              className="flex-shrink-0 w-72 h-48 relative rounded-2xl overflow-hidden shadow-lg cursor-pointer group transition-all duration-300 hover:shadow-xl active:scale-[0.98]"
            >
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt={offer.name}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : null}

              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/10" />

              {/* Badges */}
              {isZeroPriceOffer && (
                <div className="absolute top-3 right-3 flex items-center gap-1 bg-black/30 backdrop-blur-md text-white/90 px-2.5 py-1 rounded-full text-[10px] font-semibold shadow-lg border border-white/10">
                  <Megaphone className="w-3 h-3" />
                  Aviso
                </div>
              )}

              {/* Bottom overlay content */}
              <div className="absolute bottom-0 left-0 right-0 p-3.5">
                <h3 className="text-white font-bold text-sm leading-tight mb-1.5 line-clamp-2 drop-shadow-md">
                  {offer.name}
                </h3>

                {!isZeroPriceOffer ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-baseline gap-1.5">
                      {offer.products_total_amount && offer.products_total_amount > offer.price && (
                        <span className="text-white/60 text-[11px] line-through font-medium">
                          {currencySymbol}{offer.products_total_amount.toFixed(0)}
                        </span>
                      )}
                      <span className="text-white font-black text-lg drop-shadow-md">
                        {currencySymbol}{offer.price.toFixed(2)}
                      </span>
                    </div>
                    {offer.products_length ? (
                      <div className="flex items-center gap-1 text-white/60">
                        <Tag className="w-3 h-3" />
                        <span className="text-[10px] font-semibold">{offer.products_length}</span>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  offer.description ? (
                    <p className="text-white/70 text-[11px] leading-tight line-clamp-1">{offer.description}</p>
                  ) : null
                )}
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}
